import { track } from '@prism/ui/utility/analytics';
import { HttpResourceRequest } from '@angular/common/http';
import { Component, computed, inject, input, output, signal, untracked, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { ClientService } from 'src/app/services/client.service';
import { DataShareService } from 'src/app/services/data-share.service';
import { FilesService } from 'src/app/services/files.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { Action } from '../action/action';
import { IconComponent } from '../icon/icon.component';
import { ActionType, FieldType, IAction, IActionGroup, IActionOrGroup, IApiOnce, IGenericApi } from '../shared.model';
import { SideDrawerService } from '../side-drawer/side-drawer.service';
import { MatDivider } from '@angular/material/divider';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';

type MenuContentSlot =
  | { type: 'group'; label: string; actions: { action: IAction<any>; menuIndex: number }[] }
  | { type: 'action'; action: IAction<any>; menuIndex: number };

@Component({
  selector: 'pf-action-group',
  templateUrl: './action-group.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Action, IconComponent, MatMenu, MatMenuTrigger, MatButton, MatDivider, MatSlideToggle, MatTooltip]
})
export class ActionGroup {
  private readonly apiService = inject(ApiService);
  private readonly utils = inject(UtilitiesService);
  private readonly clientService = inject(ClientService);
  private readonly matDialogRef = inject(MatDialogRef, { optional: true });
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly dataShare = inject(DataShareService);
  private readonly sideDrawer = inject(SideDrawerService);
  private readonly files = inject(FilesService);

  public readonly menu = viewChild(MatMenuTrigger);
  /** Static list or (config, row) => list for row-specific menus. Resolved in action-group when componentRef has config()/row() (e.g. CellComponent). */
  public readonly actions = input.required<IAction<any>[] | ((config: any, row: unknown) => IAction<any>[])>();
  public readonly componentRef = input.required<any>();
  public readonly limit = input<number>(2);
  public readonly disabled = input<boolean>(false);
  public readonly isLoading = input<boolean>(false);
  public readonly cssClass = input<string>('');
  public readonly mixpanelEvent = input<string>();
  public readonly onAction = output<IAction<any>>();

  private readonly reloadActionTypes = [ActionType.Api, ActionType.Delete, ActionType.Save, ActionType.Upload];

  protected groupClass = computed<string>(() => {
    const cssClass = this.cssClass();
    let extraClass = ' gap-2';

    return cssClass?.includes('gap-') ? cssClass : (cssClass + extraClass);
  });
  protected actionIndex = signal<string>('-1');
  protected currentSubmenuActions = signal<IActionOrGroup<any>[]>([]);

  protected isActionGroup(item: IActionOrGroup<any>): item is IActionGroup<any> {
    return item !== null && 'groupLabel' in item && Array.isArray((item as IActionGroup).actions);
  }

  protected getMenuContentItems(items: IActionOrGroup<any>[]): MenuContentSlot[] {
    const result: MenuContentSlot[] = [];
    let menuIndex = 0;
    for (const item of items ?? []) {
      if (this.isActionGroup(item)) {
        const actionsWithIndex = item.actions.map(action => ({ action, menuIndex: menuIndex++ }));
        result.push({ type: 'group', label: item.groupLabel, actions: actionsWithIndex });
      } else {
        result.push({ type: 'action', action: item, menuIndex: menuIndex++ });
      }
    }
    return result;
  }

  protected menuAction(action: IAction<any>): IAction<any> {
    return { ...action, class: '' };
  }

  protected getActionTooltip(action: IAction<any>): string | undefined {
    const tip = action.tooltip;
    return typeof tip === 'function' ? tip(this.componentRef()) : tip ?? undefined;
  }

  protected setSubmenuActions(actions: IActionOrGroup<any>[]): void {
    this.currentSubmenuActions.set(actions ?? []);
  }

  private getContextRow(): unknown {
    const ref = this.componentRef();
    return typeof ref?.row === 'function' ? ref.row() : null;
  }

  protected getToggleChecked(action: IAction<any>): boolean {
    if (!action.toggle) return false;
    const { checked, key } = action.toggle;
    if (checked) return checked();
    const row = this.getContextRow();
    return row !== null ? !!row[key ?? 'active'] : false;
  }

  protected onToggleChange(action: IAction<any>, event: MatSlideToggleChange): void {
    const row = this.getContextRow();
    const newChecked = event.checked;
    const previousChecked = !newChecked;
    action.toggle?.callback?.(row, newChecked, {
      previousChecked,
      apply: () => (event.source.checked = newChecked),
      reset: () => (event.source.checked = previousChecked),
    });
  }

  protected resolvedActions = computed<IAction<any>[]>(() => {
    const a = this.actions();
    if (typeof a === 'function') {
      const ref = this.componentRef();
      if (ref && typeof ref.config === 'function' && typeof ref.row === 'function') {
        return a(ref.config(), ref.row()) ?? [];
      }
      return [];
    }
    return a ?? [];
  });

  protected overflowMenuItems = computed<MenuContentSlot[]>(() =>
    this.getMenuContentItems(this.resolvedActions().slice(this.limit()))
  );
  protected submenuContentItems = computed<MenuContentSlot[]>(() =>
    this.getMenuContentItems(this.currentSubmenuActions())
  );
  protected displayActions = computed<IAction<any>[]>(() =>
    this.resolvedActions().slice(0, this.limit()).reverse()
  );

  protected async actionClick(action: IAction<any>, index: number, event: Event, isMenu: boolean = false, isSubmenu: boolean = false): Promise<void> {
    event.stopPropagation();
    const { FormComponent } = await import('../form/form.component');
    const { RowFormComponent } = await import('../form/field/row-form/row-form.component');
    const { CellComponent } = await import('../datatable/cell/cell.component');
    const { DatatableComponent } = await import('../datatable/datatable.component');
    const { DataCardComponent } = await import('../data-card/data-card.component');

    if (action.type === ActionType.Api) {
      if (action.confirmation) {
        const apiOnce = action.api;
        const confirmation = typeof action.confirmation === 'function' ? action.confirmation(this.componentRef()) : action.confirmation;
        const confirmationAction = {
          ...confirmation.action,
          type: ActionType.Api
        };
        confirmationAction.api = {
          ...apiOnce,
          payload: untracked(() => typeof apiOnce.payload === 'function' ? apiOnce.payload(this.componentRef()) : apiOnce.payload)
        }
        this.utils.openConfirmationDialog(confirmation.title, confirmation.message, confirmationAction, confirmation.irreversible, confirmation.icon);
      } else {
        this.apiOnce(action, index, isMenu, isSubmenu);
      }
    } else if (action.type === ActionType.Save) {
      const componentRef = this.componentRef();
      let formRef: InstanceType<typeof FormComponent>;

      if (componentRef instanceof FormComponent) {
        formRef = componentRef;
      } else {
        formRef = componentRef.formRef();
      }

      const message: string = action.type + 'd';
      const entity: string = formRef.config()?.header?.title || 'Data';
      const payload: HttpResourceRequest = formRef.getSaveApiPayload();
      const apiOnce: IApiOnce = {
        payload,
        success: formRef.config().saveApi?.success || `${entity} has been ${message} successfully.`,
        error: formRef.config().saveApi?.error || `${entity} could not be ${message}.`
      };
      const isFormValid = await formRef.onSave();
      if (!isFormValid) return;

      const confirmation = typeof action.confirmation === 'function' ? action.confirmation(this.componentRef()) : action.confirmation;
      if (confirmation) {
        const confirmationAction = {
          type: ActionType.Api,
          ...confirmation.action
        };
        confirmationAction.api = { ...apiOnce };
        this.utils.openConfirmationDialog(confirmation.title, confirmation.message, confirmationAction, confirmation.irreversible, confirmation.icon);
      } else {
        this.apiOnce({ ...action, api: apiOnce }, index, isMenu, isSubmenu);
      }
    } else if (action.type === ActionType.Delete) {
      let deleteConfig: IGenericApi = action.deleteApi;
      let entity: string;
      let recordName: string;
      let idField: string;
      let id: string | number;

      this.menu()?.closeMenu?.();

      if (this.componentRef() instanceof FormComponent) {
        const componentRef = this.componentRef() as InstanceType<typeof FormComponent>;
        entity = componentRef.config()?.header?.title || 'Data';
        deleteConfig = componentRef.config()?.saveApi;
        deleteConfig.payload = componentRef.config().deleteValue;
        idField = componentRef.config().idField || 'id';
        id = componentRef.id();
        
        if (!id) {
          this.onAction.emit(action);
          return;
        }
      } else if (this.componentRef() instanceof CellComponent) {
        const componentRef = this.componentRef() as InstanceType<typeof CellComponent>;
        idField = componentRef.datatableConfig().idField || 'id';
        entity = this.utils.resolveHeaderConfig(componentRef.datatableConfig().header)?.title || 'Record';
        recordName = componentRef.row()['name'] || '';
        id = componentRef.row()[idField];
      } else if (this.componentRef() instanceof DataCardComponent) {
        const componentRef = this.componentRef() as InstanceType<typeof DataCardComponent>;
        idField = 'id';
        entity = componentRef.parentConfig()?.header?.title || 'Record';
        recordName = componentRef.title() || '';
        id = componentRef.data()?.id;
      }

      let payload: HttpResourceRequest = this.getDeletePayload(deleteConfig, idField, id);
      this.openDeleteDialog(payload, entity, action, recordName);
    } else if (action.type === ActionType.AddRow) {
      const componentRef = this.componentRef();
      let rowFormRef: InstanceType<typeof RowFormComponent>;

      if (componentRef instanceof FormComponent) {
        const index = componentRef.config().fields.findLastIndex(field => field.type === FieldType.RowForm);
        rowFormRef = componentRef.fieldRef()[index].rowForm();
      } else if (componentRef instanceof RowFormComponent) {
        rowFormRef = componentRef;
      }

      rowFormRef.addRow();
      this.menu()?.closeMenu?.();
      this.closeParent(action);
    } else if (action.type === ActionType.Download) {
      const apiOnceConfig: IApiOnce = action.api;
      let entity: string = action.fileName;
      let fileName: string = entity;
      const componentRef = this.componentRef();
      const resolvedPayload = typeof apiOnceConfig?.payload === 'function'
        ? apiOnceConfig.payload(componentRef)
        : apiOnceConfig?.payload;
      let payload: HttpResourceRequest = { ...(resolvedPayload as HttpResourceRequest) };

      if (this.componentRef() instanceof DatatableComponent) {
        const datatableRef = this.componentRef() as InstanceType<typeof DatatableComponent>;
        payload = { ...this.utils.genericGetApiPayload(datatableRef.config()), ...payload };

        if (!entity) entity = this.utils.resolveHeaderConfig(datatableRef?.config?.()?.header)?.title || 'file';
        fileName = entity + '.csv';
      }

      const downloadApi: IApiOnce = {
        ...apiOnceConfig,
        payload,
        responseType: apiOnceConfig?.responseType ?? 'blob',
        success: apiOnceConfig?.success ?? ((response) => {
          this.utils.downloadFile(response?.path ?? response?.data?.presignedUrl ?? response?.s3PreSignedURL ?? response?.file ?? response, response?.fileName ?? fileName);
          this.toastService.showToast({ type: 'success', msg: `${entity || 'File'} has been downloaded successfully.` });
        }),
        error: apiOnceConfig?.error ?? `Failed to download ${entity || 'file'}. Please try again later.`,
      };

      this.apiOnce({ ...action, api: downloadApi }, index, isMenu, isSubmenu);
    } else if (action.type === ActionType.Upload) {
      const componentRef = this.componentRef() as InstanceType<typeof FormComponent>;
      const formValue = componentRef.formValue();
      const file = formValue?.file;

      if (!file || !action.uploadConfig) return;

      const uploadFile = () => {
        this.actionIndex.set((isSubmenu ? 'SUBMENU' : (isMenu ? 'MENU' : 'ROW')) + index);
        const uploadConfig = { ...action.uploadConfig, files: [file], hideLoader: true, complete: (success: boolean) => {
          this.onComplete(action);
          componentRef.fieldRef().forEach(field => {
            if (field.fileRef()) {
              field.fileRef().clearInput();
              field.fileRef().markAsUntouched();
              field.fileRef().markAsPristine();
            }
          });
        } };

        delete formValue.file;
        uploadConfig.uploadAction = { ...uploadConfig?.uploadAction, payload: { ...uploadConfig?.uploadAction?.payload, ...formValue } };
        this.files.uploadFiles(uploadConfig);
      }

      const confirmation = typeof action.confirmation === 'function' ? action.confirmation(this.componentRef()) : action.confirmation;
      if (confirmation) {
        const confirmationAction = {
          ...confirmation.action,
          callback: () => uploadFile()
        };
        this.utils.openConfirmationDialog(confirmation.title, confirmation.message, confirmationAction, confirmation.irreversible, confirmation.icon);
      } else {
        uploadFile();
      }
    } else if (action.type === ActionType.Dialog) {
      this.menu()?.closeMenu?.();
      const dialog = typeof action.dialog === 'function' ? await action.dialog(this.componentRef()) : action.dialog;

      if (this.componentRef() instanceof DataCardComponent) {
        const componentRef = this.componentRef() as InstanceType<typeof DataCardComponent>;
        if (dialog.config?.custom) dialog.config.inputData = { dialogData: componentRef.data() };
      }
      
      this.utils.openDialog(dialog.config, dialog.height, dialog.width, dialog.customClass);
      this.closeParent(action);
    } else if (action.type === ActionType.SideDrawer) {
      this.menu()?.closeMenu?.();
      const sideDrawer = typeof action.sideDrawer === 'function' ? action.sideDrawer(this.componentRef()) : action.sideDrawer;
      this.sideDrawer.openSideDrawer(sideDrawer);
    } else if (action.type === ActionType.Route) {
      this.menu()?.closeMenu?.();
      this.router.navigate([action.route]);
    } else if (action.type === ActionType.Custom || !action.type) {
      if (action.callback) {
        action.callback?.(this.componentRef());
      }

      this.onAction.emit(action);
      this.menu()?.closeMenu?.();
      this.closeParent(action);
    } else {
      this.onAction.emit(action);
      this.menu()?.closeMenu?.();
      this.closeParent(action);
    }

    this.trackMixpanel(action);
  }

  protected isActionDisabled(action: IAction<any>): boolean {
    return this.disabled() || this.actionIndex() !== '-1' || (typeof action.disabled === 'function' ? action.disabled(this.componentRef()) : action.disabled);
  }

  private getDeletePayload(deleteConfig: IGenericApi, idField: string, id: string | number): HttpResourceRequest {
    const clientId = this.clientService.clientId();
    const extraPayload = deleteConfig?.payload;
    let payload: HttpResourceRequest = {
      url: deleteConfig?.endpoint,
      body: {}
    };
    if (deleteConfig?.useAptHttpMethod) {
      payload.method = 'DELETE';
      payload.url += '/' + id;
    } else {
      payload.body = { disable: 1 };
      payload.body[idField] = id;
    }
    
    if (deleteConfig?.clientId) payload.body['clientId'] = clientId;

    if (extraPayload) {
      if (typeof extraPayload === 'function') {
        payload.body = extraPayload(payload.body);
      } else {
        payload.body = { ...(payload.body as any), ...extraPayload };
      }
    }

    return payload;
  }

  private openDeleteDialog(payload: HttpResourceRequest, entity: string, action: IAction<any>, recordName?: string): void {
    const apiOnce: IApiOnce = {
      payload: payload,
      success: `${entity} has been deleted successfully.`,
      error: `${entity} could not be deleted. Please try again later.`,
      complete: () => {
        if (action.callback) {
          action.callback?.(this.componentRef());
        }
        this.onAction.emit(action);
      }
    };
    const confirmation = typeof action.confirmation === 'function' ? action.confirmation(this.componentRef()) : action.confirmation;
    const message: string = confirmation?.message || `Are you sure you want to delete this ${entity.toLowerCase()}?`;
    const title: string = confirmation?.title || `Delete ${recordName ?? entity.toLowerCase()}`;
    const deleteAction: IAction<any> = { name: 'Delete', type: ActionType.Api, api: apiOnce, ...confirmation?.action };

    this.utils.openConfirmationDialog(title, message, deleteAction, true);
  }

  private apiOnce(action: IAction<any>, index: number, isMenu: boolean = false, isSubmenu: boolean = false): any {
    this.actionIndex.set((isSubmenu ? 'SUBMENU' : (isMenu ? 'MENU' : 'ROW')) + index);
    const apiOnce = action.api;
    const payload = untracked(() => typeof apiOnce.payload === 'function' ? apiOnce.payload(this.componentRef()) : apiOnce.payload);

    this.apiService.apiSignalOnce(payload, apiOnce.success, apiOnce.error, (success) => this.onComplete(action, apiOnce, success), apiOnce?.responseType);
  }

  private onComplete(action: IAction<any>, apiOnce?: IApiOnce, success?: boolean): void {
    if (apiOnce) apiOnce.complete?.();
    this.actionIndex.set('-1');
    this.onAction.emit(action);
    this.menu()?.closeMenu?.();
    this.closeParent(action);
    if (this.reloadActionTypes.some(type => type === action.type) && success) this.dataShare.reloadPage.set(true);
  }

  private closeParent(action: IAction<any>): void {
    if (action.disableClose) return;

    this.matDialogRef?.close?.();
    this.sideDrawer.closeSideDrawer();
  }

  /******* Analytics Method ********/
  private trackMixpanel(action: IAction<any>): void {
    const mixpanelEvent = this.mixpanelEvent() || action.mixpanelEvent;

    if (mixpanelEvent) {
      if (action.type === ActionType.Route) {
        track(mixpanelEvent, { page: action.route });
      } else {
        track(mixpanelEvent, { userAction: action.name });
      }
    }
  }
}
